import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Link from 'next/link';
import Router from 'next/router';
import { ascending } from 'd3';
import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import { IAreaGraph, IContributor } from '@/types/explorer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/shadcn/breadcrumb';
import { sanitizeUsername } from '@/utils/strings';
import useExplorerData from '@/hooks/useExplorerData';
import AreaChart from '@/components/ui/charts/AreaChart';
import { prettyDate } from '@/utils/dates';
import { getContributors } from '@/api/contributors';
import { Button } from '@/components/shadcn/button';
import { P } from '@/components/shadcn/typography';
import CustomTooltip from '../charts/CustomTooltip';

const LIMIT = 100;

const columnHelper = createColumnHelper<IContributor>();
const cols = [
  {
    id: 'username',
    header: 'Contributor',
    enableSorting: true,
    cell: ({ row }: { row: Row<IContributor> }) => (
      <div className="text-xs md:text-sm">
        {sanitizeUsername(row.getValue('username'))}
      </div>
    ),
  },
  {
    id: 'createdAt',
    header: 'Joined',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<IContributor> }) => (
      <div className="text-xs md:text-sm">
        {prettyDate(row.getValue('createdAt'))}
      </div>
    ),
  },
  {
    id: 'lastSeen',
    header: 'Last Seen',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<IContributor> }) => (
      <div className="text-xs md:text-sm">
        {prettyDate(row.getValue('lastSeen'))}
      </div>
    ),
  },
];

export default function Contributors() {
  const { data: explorerData } = useExplorerData();
  const [contributorsList, setContributors] = useState<IContributor[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: LIMIT,
    pageIndex: 0,
  });
  const [sorting, setSorting] = useState([{ id: 'lastSeen', desc: true }]);
  const order = !sorting.length ? 'DESC' : sorting[0].desc ? 'DESC' : 'ASC';
  const field = sorting.length ? sorting[0].id : 'DESC';
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const columns = useMemo(
    () =>
      cols.map(({ id, header, cell, enableSorting, sortDescFirst }: any) => ({
        ...columnHelper.accessor(id, {
          header,
        }),
        cell,
        enableSorting,
        sortDescFirst,
      })),
    [],
  );

  const fetchList = useCallback(
    async (skip = 0, orderBy: string, order: 'ASC' | 'DESC') => {
      setIsLoading(true);
      const contributors: IContributor[] = await getContributors(
        LIMIT,
        skip,
        orderBy,
        order.toLowerCase() === 'asc',
      );
      setHasNextPage(contributors.length === LIMIT);
      setContributors(contributors);
      setIsDataLoaded(true);
      setIsLoading(false);
    },
    [setContributors],
  );

  useEffect(() => {
    fetchList(pagination.pageIndex * LIMIT, field, order);
  }, [fetchList, pagination.pageIndex, field, order]);

  let graphData: IAreaGraph[] = [];

  const stats = explorerData?.stats;

  const total = stats?.byDay?.[stats?.mostRecentDay]?.totalContributors || 0;
  if (stats) {
    graphData = Object.entries(stats?.byDay)
      .map(([date, value]) => ({
        date,
        value: value.totalContributors,
      }))
      .sort((a, b) => ascending(a.date, b.date));
  }

  const tableData = React.useMemo(
    () => (isLoading ? [] : contributorsList),
    [isLoading, contributorsList],
  );

  const table = useReactTable({
    data: tableData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    rowCount: total,
    state: {
      pagination,
      sorting: [
        {
          id: field,
          desc: order === 'DESC',
        },
      ],
    },
  });

  const from =
    table.getState().pagination.pageIndex *
      table.getState().pagination.pageSize +
    1;
  const to =
    from +
    Math.min(
      (table.getState().pagination.pageIndex + 1) *
        table.getState().pagination.pageSize,
      contributorsList.length - 1,
    );

  const averageContributors = useMemo(() => {
    if (!stats?.byDay) {
      return {
        last7Days: 0,
        last30Days: 0,
      };
    }
    const last7DaysStats = Object.values(stats.byDay).slice(-7);
    const last30DaysStats = Object.values(stats.byDay).slice(-30);

    const active7Days = last7DaysStats.reduce(
      (acc, day) => acc + day.totalContributors,
      0,
    );
    const active30Days = last30DaysStats.reduce(
      (acc, day) => acc + day.totalContributors,
      0,
    );
    return {
      last7Days: active7Days / 7,
      last30Days: active30Days / 30,
    };
  }, [stats]);

  const chartDataCards = [
    {
      title: 'Active the Last 7 Days',
      value: averageContributors.last7Days,
    },
    {
      title: 'Active the Last 30 Days',
      value: averageContributors.last30Days,
    },
  ];

  return (
    <div className="flex h-full flex-col gap-2">
      <Breadcrumb className="flex h-6 w-full items-center md:hidden">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link href="">Network</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Contributors</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex h-full max-w-2xl grow-0 flex-col-reverse justify-start gap-2 pb-4 md:size-full md:gap-6 md:pb-8 lg:flex-row">
        <div className="no-scrollbar flex w-full flex-col gap-2 text-neutral-0 md:gap-4 lg:basis-1/2 lg:overflow-auto">
          <Breadcrumb className="hidden h-9 shrink-0 items-center md:flex">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="">Network</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Contributors</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex h-9 w-full items-center md:hidden">
            <P>Global contributors are building the world’s freshest map.</P>
          </div>
          <Card className="flex h-[300px] w-full flex-col gap-2 overflow-hidden border border-neutral-800 bg-neutral-900 p-4 md:h-full md:rounded-lg md:p-6">
            <CardHeader className="p-0 text-sm md:text-base">
              <CardTitle>Contributors</CardTitle>
            </CardHeader>
            <CardContent className="flex h-full flex-col p-0 md:min-h-[200px]">
              <p className="mb-1 hidden text-base font-thin text-azure-400 md:block md:text-3xl">
                {Math.round(total).toLocaleString('en-US')}
              </p>
              <div className="flex size-full">
                <AreaChart
                  showYAxis
                  xAxisProps={{
                    padding: { right: 10 },
                    angle: -45,
                  }}
                  yAxisProps={{
                    domain: ['dataMin - 5000', 'dataMax'],
                  }}
                  margin={{
                    left: -20,
                  }}
                  data={graphData}
                  customTooltip={(props: any) => (
                    <CustomTooltip
                      line1={`${Math.round(
                        props?.payload[0]?.value || 0,
                      ).toLocaleString('en-US')} Contributors`}
                      {...props}
                    />
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="flex w-full flex-col gap-2 md:h-1/2 md:gap-4 lg:h-full lg:basis-1/2">
          <div className="hidden h-9 w-full items-center md:flex">
            <P>Global contributors are building the world’s freshest map.</P>
          </div>
          <Card className="no-scrollbar h-[360px] overflow-scroll border border-neutral-800 bg-neutral-900 md:flex-1 [&>div]:overflow-visible">
            <Table className="h-full table-fixed">
              <TableHeader className="sticky top-0 border-neutral-800 bg-neutral-900">
                {table.getHeaderGroups().map(headerGroup => (
                  <TableRow
                    className="border-neutral-800 hover:bg-neutral-900"
                    key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <TableHead
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                          data-order={index}
                          className="h-4 w-1/4 cursor-pointer p-2 data-[order='0']:w-1/2 md:h-12 md:px-4">
                          <div className="flex cursor-pointer items-center gap-1 px-0 text-[10px] leading-none text-neutral-0 md:text-sm">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}

                            {header.column.getIsSorted() === 'asc' ? (
                              <CaretUpIcon />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <CaretDownIcon />
                            ) : null}
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="border-neutral-800 bg-neutral-900">
                {table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer border-neutral-800 leading-none hover:bg-neutral-800"
                    onClick={() =>
                      Router.push(
                        `/network/contributors/${row.getValue('username')}`,
                      )
                    }>
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="p-2 md:px-4 md:py-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          {isDataLoaded ? (
            <Card className="flex w-full items-center justify-end space-x-2 rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 text-neutral-0 md:p-4">
              <P className="flex-1 text-2xs md:text-xs">
                {from} to {to} of {total} contributors
              </P>
              <div className="space-x-2">
                <Button
                  variant="default"
                  size="sm"
                  className="text-[10px] md:text-xs"
                  onClick={table.previousPage}
                  disabled={!table.getCanPreviousPage()}>
                  Previous
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="text-[10px] md:text-xs"
                  onClick={table.nextPage}
                  disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
